import "../css/index.css"
import {soundPlay} from "./sound"
import * as THREE from "three"
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls"
import {GLTFLoader} from "three/examples/jsm/loaders/GLTFLoader.js"
import {DRACOLoader} from "three/examples/jsm/loaders/DRACOLoader"
import {InteractionManager} from "three.interactive"
import gsap from "gsap"
import changeLight from "./light"
//import greenRoof from "./greenroof"

import controlPanel from "./controlpanel"
import Districts from "./districthover"
import Sky from "./sky"

import livingPlaces from "./heatpoints/livingplaces"
import oldCity from "./heatpoints/oldcity"
import fabric from "./heatpoints/fabric"
import bridge from "./heatpoints/bridge"
import hospital from "./heatpoints/hospital"
import VerticalFarm from "./heatpoints/verticalfarm"
import quanTower from "./heatpoints/quantower"
import bikeBridge from "./heatpoints/bikebridge"
import smarteproduktion from "./heatpoints/smarteproduktion"
import greenStreets from "./heatpoints/greenstreets"

class useWebGL {
	constructor() {
		this.canvas = document.querySelector("canvas.map")
		this.clock = new THREE.Clock()
		this.width = this.canvas.clientWidth
		this.height = this.canvas.clientHeight
		this.self = this
		this.camera
		this.controls

		this.previousTime = null
		this.map = null
		this.pin = null
		this.mixer = null
		this.sceneReady = false
		this.modalOpen = false
		this.visitedPinColor = 0x2785a5
		this.pinColor = 0x004f9f
		this.pinColorDark = 0x002f9f

		// Functions
		this.createScene()
		this.createCamera()
		this.createLightEffects()
		this.createRenderer()
		this.onResize()
		this.createModel()
		this.update()
		this.addEventListeners()
		this.init()
	}

	createScene() {
		this.scene = new THREE.Scene()
		//this.scene.background = new THREE.Color(0xb2dded)

		this.loadingManager = new THREE.LoadingManager(
			// Loaded
			() => {
				// Wait a little
				window.setTimeout(() => {
					const exploreButton = document.getElementById("exploreButton")
					exploreButton.disabled = false
					exploreButton.classList.remove("animate-pulse")
					const loaderspin = document.getElementById("loaderspin")
					loaderspin.classList.add("hidden")
					const pulse = document.getElementById("pulse")
					pulse.classList.remove("hidden")
					const buttonText = document.getElementById("exploreButtonText")
					buttonText.innerHTML = "Start"
				}, 500)
				window.setTimeout(() => {
					this.sceneReady = true
				}, 3000)
			}
		)
	}

	createRenderer() {
		this.renderer = new THREE.WebGLRenderer({
			canvas: this.canvas,
			powerPreference: "high-performance",
			antialias: true,
			alpha: false
		})
		const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

		this.renderer.setSize(this.width, this.height)
		this.renderer.setPixelRatio(isMobile ? 1 : Math.min(window.devicePixelRatio, 2))
		this.renderer.outputEncoding = THREE.sRGBEncoding

		this.renderer.physicallyCorrectLights = !isMobile // Disable on mobile
		this.renderer.toneMapping = isMobile ? THREE.NoToneMapping : THREE.ACESFilmicToneMapping
		// this.renderer.toneMapping = THREE.CineonToneMapping
		this.renderer.toneMappingExposure = 1.5
		this.renderer.shadowMap.enabled = !isMobile
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
		this.renderer.frustumCulling = true

		// Controls
		this.controls = new OrbitControls(this.camera, this.renderer.domElement)
		this.controls.enableDamping = true
		this.controls.enablePan = false
		this.controls.enableZoom = true
		this.controls.zoomSpeed = -0.3 // Reverse direction of mouse Zoom in & out
		this.controls.autoRotate = false
		this.controls.autoRotateSpeed = 0.5
		this.controls.maxPolarAngle = Math.PI / 2.4
		this.controls.minPolarAngle = Math.PI / 5
		this.controls.minDistance = 2.3
		this.controls.maxDistance = 40
	}

	createCamera() {
		this.camera = new THREE.PerspectiveCamera(75, this.width / this.height, 0.2, 1000)
		this.camera.position.set(0, 15, 0)
		this.camera.updateProjectionMatrix()
		this.scene.add(this.camera)
	}

	createLightEffects() {
		this.sky = new Sky()
		this.sky.rotation.set(0, 0, 0)
		this.scene.add(this.sky)

		//Fog
		this.fog = new THREE.FogExp2(0xb2dded, 0.1)
		this.scene.fog = this.fog
		// Daylight
		this.hemiLight = new THREE.HemisphereLight(0x000000, 0xffffff, 0)
		this.hemiLight.color.setHSL(0.6, 10, 0.6)
		this.hemiLight.groundColor.setHSL(0.095, 1, 0.75)
		this.hemiLight.position.set(0, 10, 0)
		this.scene.add(this.hemiLight)

		this.dirLight = new THREE.DirectionalLight(0xe8c37b, 1.0)
		this.dirLight.color.setHSL(0.1, 1, 0.95)
		this.dirLight.position.set(-69, 44, 14)
		this.dirLight.castShadow = true
		this.dirLight.shadow.mapSize.width = 1024
		this.dirLight.shadow.mapSize.height = 1024
		this.dirLight.shadow.camera.far = 45
		this.dirLight.shadowDarkness = 1
		this.dirLight.shadow.normalBias = 0.05

		this.scene.add(this.dirLight)
		// External Animation
		const lightChange = new changeLight(this.hemiLight, this.dirLight, this.worldsky, this.fog, this.dirLight.position, (topColor, bottomColor) => {
			this.sky.updateSkyColor(topColor, bottomColor)
		})
	}

	createModel() {
		const textureLoader = new THREE.TextureLoader()
		const greenstreet = textureLoader.load("./dist/texture/greenstreet4.webp")

		this.interactionManager = new InteractionManager(this.renderer, this.camera, this.renderer.domElement)

		// City
		this.gltfLoader = new GLTFLoader(this.loadingManager)
		this.dracoLoader = new DRACOLoader()
		this.dracoLoader.setDecoderPath("node_modules/three/examples/jsm/libs/draco/")
		this.dracoLoader.setDecoderConfig({type: "js"})
		this.gltfLoader.setDRACOLoader(this.dracoLoader)

		this.gltfLoader.load("model/3DStadt.glb", (gltf) => {
			gltf.scene.scale.set(0.06, 0.06, 0.06)
			this.mixer = new THREE.AnimationMixer(gltf.scene)

			gltf.scene.traverse(function (child) {
				if (child.isMesh) {
					child.frustumCulled = false
					child.castShadow = true
					child.receiveShadow = true
					if (child.name === "Terrain_mit_stadtteile") {
						child.geometry.computeVertexNormals()
						child.material.flatShading = false
					} else {
						if (child.name != "Fluss001") {
							child.geometry.computeVertexNormals()
						}
					}
				}
			})

			// River Color
			gltf.scene.children[33].material.color.setHex(0x9cd3db)
			gltf.scene.children[33].material.metalness = 0.5
			gltf.scene.children[33].material.roughness = 0.5
			gltf.scene.children[33].material.needsUpdate = true
			gltf.scene.children[33].geometry.computeVertexNormals()
			gltf.scene.children[33].material.flatShading = false

			// Quantower
			this.quanTower = new quanTower(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.animations,
				this.mixer,
				gltf.scene,
				this.visitedPinColor
			)

			// Vertical Farm
			this.verticalFarm = new VerticalFarm(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.animations,
				this.mixer,
				this.visitedPinColor
			)

			// WOHNHAUS – DIGITALISIERUNG
			this.livingPlaces = new livingPlaces(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				this.visitedPinColor
			)

			// Altstadt
			this.oldCity = new oldCity(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				this.visitedPinColor
			)

			// Wellness center
			this.hospital = new hospital(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.animations,
				this.mixer,
				this.visitedPinColor
			)

			//Fabrik
			this.fabric = new fabric(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.scene,
				gltf.animations,
				this.mixer,
				this.visitedPinColor
			)

			// Green Streets
			this.greenStreets = new greenStreets(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				greenstreet,
				gltf.scene,
				this.visitedPinColor
			)

			// Brücke Heatpoint
			this.bridge = new bridge(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.animations,
				this.mixer,
				this.visitedPinColor
			)

			this.bikebridge = new bikeBridge(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.animations,
				this.mixer,
				this.visitedPinColor
			)

			// Smarte Produktion (tattoine)
			this.smarteproduktion = new smarteproduktion(
				this.pinColor,
				this.pinColorDark,
				this.camera,
				this.interactionManager,
				() => {
					this.controls.autoRotate = false
					this.modalOpen = true
				},
				gltf.animations,
				this.mixer,
				this.visitedPinColor
			)

			const controlpanel = new controlPanel(
				// Digitalisierung
				() => {
					// on
					gsap.to(this.smarteproduktion.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.quanTower.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.hospital.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.livingPlaces.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.oldCity.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.greenStreets.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})

					// off
					gsap.to(this.fabric.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bikebridge.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bridge.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.verticalFarm.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
				},
				// Nachhaltigkeit
				() => {
					// on
					gsap.to(this.smarteproduktion.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bikebridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.greenStreets.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.fabric.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.verticalFarm.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})

					// off
					gsap.to(this.livingPlaces.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.oldCity.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bridge.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.quanTower.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.hospital.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
				},
				// Individuelle Prozesse
				() => {
					//on
					gsap.to(this.smarteproduktion.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.oldCity.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.livingPlaces.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.fabric.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})

					// off
					gsap.to(this.verticalFarm.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.greenStreets.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.quanTower.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.hospital.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bikebridge.position, {y: -1, duration: 2.5, ease: "Power3.inOut"})
				},
				// reset
				() => {
					this.quanTower.resetAnimations(gltf.animations, this.mixer, gltf.scene)
					this.verticalFarm.resetAnimations(gltf.animations, this.mixer)
					this.bridge.resetAnimations(gltf.animations, this.mixer)
					this.hospital.resetAnimations(gltf.animations, this.mixer)
					this.greenStreets.resetAnimations(gltf.scene)
					this.fabric.resetAnimations(gltf.scene, gltf.animations, this.mixer)
					this.bikebridge.resetAnimations(gltf.animations, this.mixer)
					this.smarteproduktion.resetAnimations(gltf.animations, this.mixer)
					gsap.to(this.fabric.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.livingPlaces.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.greenStreets.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.quanTower.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.hospital.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.oldCity.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.verticalFarm.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bikebridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.smarteproduktion.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
				},
				() => {
					this.quanTower.resetAnimations(gltf.animations, this.mixer, gltf.scene)
					this.verticalFarm.resetAnimations(gltf.animations, this.mixer)
					this.bridge.resetAnimations(gltf.animations, this.mixer)
					this.hospital.resetAnimations(gltf.animations, this.mixer)
					this.greenStreets.resetAnimations(gltf.scene)
					this.fabric.resetAnimations(gltf.scene, gltf.animations, this.mixer)
					this.bikebridge.resetAnimations(gltf.animations, this.mixer)
					this.smarteproduktion.resetAnimations(gltf.animations, this.mixer)
					gsap.to(this.fabric.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.livingPlaces.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.greenStreets.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.quanTower.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.hospital.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.oldCity.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.verticalFarm.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bikebridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.smarteproduktion.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
				},
				() => {
					this.quanTower.triggerAnimations(gltf.animations, this.mixer, false, gltf.scene)
					this.verticalFarm.triggerAnimations(gltf.animations, this.mixer, false)
					this.bridge.triggerAnimations(gltf.animations, this.mixer, false)
					this.hospital.triggerAnimations(gltf.animations, this.mixer, false)
					this.greenStreets.triggerAnimations(greenstreet, gltf.scene, false)
					this.fabric.triggerAnimations(gltf.scene, false, gltf.animations, this.mixer)
					this.smarteproduktion.triggerAnimations(gltf.animations, this.mixer, false)
					gsap.to(this.fabric.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.livingPlaces.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.greenStreets.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.quanTower.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.hospital.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.oldCity.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.verticalFarm.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.bikebridge.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
					gsap.to(this.smarteproduktion.position, {y: 0, duration: 2.5, ease: "Power3.inOut"})
				}
			)

			this.map = gltf.scene
			this.map.position.set(-4, 0, 0)
			this.scene.add(
				this.map,
				this.quanTower,
				this.verticalFarm,
				this.livingPlaces,
				this.oldCity,
				this.hospital,
				this.fabric,
				this.greenStreets,
				this.bridge,
				this.bikebridge,
				this.smarteproduktion
			)
		})
	}

	addEventListeners() {
		const self = this
		window.addEventListener(
			"resize",
			this.debounce(() => this.onResize(), 150)
		)

		const modals = [
			document.getElementById("modal1"),
			document.getElementById("modal2"),
			document.getElementById("modal3"),
			document.getElementById("modal4"),
			document.getElementById("modal5"),
			document.getElementById("modal6"),
			document.getElementById("modal7")
		]
		const {camera, controls} = this

		document.body.addEventListener("click", function (event) {
			const isModalClose = event.target.closest(".modal-close")
			const isModalOverlay = event.target.closest(".modal-overlay")

			if (isModalClose || isModalOverlay) {
				modals.forEach((modal) => {
					modal.classList.add("hidden")
				})
				controls.autoRotate = false
				self.modalOpen = false
				gsap.to(camera.position, {y: camera.position.y + 0.5, duration: 2.5, ease: "Power3.inOut"})

				// Prevent further propagation of the event
				event.stopPropagation()
			}
		})
	}

	debounce(func, wait) {
		let timeout
		return function (...args) {
			clearTimeout(timeout)
			timeout = setTimeout(() => func.apply(this, args), wait)
		}
	}

	onResize() {
		this.screen = {
			width: window.innerWidth,
			height: window.innerHeight
		}
		this.camera.aspect = this.screen.width / this.screen.height
		this.camera.updateProjectionMatrix()
		this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
		this.renderer.setSize(this.screen.width, this.screen.height)
	}

	init() {
		const {canvas, camera, controls} = this
		const instructions = document.getElementById("instructions")
		const instruction1 = document.getElementById("instruction1")
		const instruction2 = document.getElementById("instruction2")
		const overlay = document.getElementById("overlay")
		const exploreButton = document.getElementById("exploreButton")

		const handleTimeout = () => {
			// console.clear();
		}

		const handleClick = () => {
			instructions.classList.remove("visible")
			instructions.classList.add("incative")
			instruction1.classList.remove("visible")
			instruction1.classList.add("incative")
			instruction2.classList.remove("visible")
			instruction2.classList.add("incative")
			setTimeout(() => {
				instructions.classList.add("hidden")
				instruction1.classList.add("hidden")
				instruction2.classList.add("hidden")
				instruction1.remove()
				instruction2.remove()
			}, 500)

			canvas.removeEventListener("click", handleClick)
		}

		const handleCamera = (event) => {
			if (!this.modalOpen) {
				controls.autoRotate = false
				clearTimeout(this.autoRotateTimeout)
				this.autoRotateTimeout = setTimeout(() => {
					if (!this.modalOpen) {
						controls.autoRotate = false
					}
				}, 18000)
			}
			if (event.type === "click") {
				handleClick()
			}
		}

		const handleExploreButtonClick = () => {
			overlay.classList.add("hidden")
			this.controls.autoRotate = false
			gsap.to(camera.position, {x: 3, y: 1.5, z: 2, duration: 3, ease: "Power3.inOut"})
			setTimeout(() => {
				controls.maxDistance = 4
			}, 4000)
		}

		canvas.addEventListener("click", handleCamera)
		canvas.addEventListener("mousemove", handleCamera)
		canvas.addEventListener("click", handleClick)
		exploreButton.addEventListener("click", handleExploreButtonClick)

		setTimeout(handleTimeout, 2000)
	}

	update() {
		if (this.sceneReady) {
			if (this.mixer && this.mixer._actions.some((action) => action.isRunning())) {
				this.elapsedTime = this.clock.getElapsedTime()
				this.deltaTime = this.elapsedTime - this.previousTime
				this.previousTime = this.elapsedTime
				this.mixer.update(this.deltaTime)
			}
		}
		this.interactionManager.update()
		this.controls.update()
		this.renderer.render(this.scene, this.camera)

		requestAnimationFrame(() => {
			this.update()
		})
	}
}

Districts()
window.addEventListener("load", () => {
	setTimeout(() => {
		soundPlay()
		new useWebGL()
	}, 2000)
})
